// src/api/audio.js
const API_URL = process.env.REACT_APP_API_URL || 'https://api.display-promo.ru';

let token = null;

/* -------- auth helpers -------- */
export function setToken(newToken) {
    token = newToken;
    if (newToken) localStorage.setItem('jwt', newToken);
    else localStorage.removeItem('jwt');
}
export function getToken() {
    return token || localStorage.getItem('jwt');
}
function authHeaders(extra = {}) {
    const h = { ...extra };
    const t = getToken();
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
}
async function handle(res) {
    if (res.status === 401) {
        setToken(null);
        window.location.href = '/login';
        throw new Error('Unauthorized');
    }
    if (!res.ok) {
        let msg = 'API error';
        try { const j = await res.json(); msg = j.error || j.message || msg; } catch {}
        throw new Error(msg);
    }
    if (res.status === 204) return null;
    const ct = res.headers.get('content-type') || '';
    return ct.includes('application/json') ? res.json() : res.text();
}

/* -------- Аудиофайлы --------
   Роуты:
   GET    /admin/audio/resources
   POST   /admin/audio/upload
   POST   /admin/audio/uploads
   DELETE /admin/audio/resource/{id}
-------------------------------- */
export async function audioList() {
    const res = await fetch(`${API_URL}/admin/audio/resources`, { headers: authHeaders() });
    return handle(res);
}
export async function audioDelete(id) {
    const res = await fetch(`${API_URL}/admin/audio/resource/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
    });
    return handle(res);
}
export async function audioUpload(file, onProgress) {
    const form = new FormData();
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    const url = `${API_URL}/admin/audio/upload`;
    return new Promise((resolve, reject) => {
        xhr.open('POST', url, true);
        const t = getToken();
        if (t) xhr.setRequestHeader('Authorization', `Bearer ${t}`);
        xhr.upload.onprogress = (e) => {
            if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
            const ok = xhr.status >= 200 && xhr.status < 300;
            if (!ok) {
                let msg = 'Upload failed';
                try { msg = JSON.parse(xhr.responseText).error || msg; } catch {}
                reject(new Error(msg));
                return;
            }
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (e) { reject(e); }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(form);
    });
}
/** Массовая загрузка: отправка по одному файлу для персонального прогресса */
export async function audioUploadMany(files, onEachProgress) {
    const results = [];
    for (let i = 0; i < files.length; i++) {
        // можно заменить на единый POST /admin/audio/uploads, если не нужен пер-файл прогресс
        /* альтернативно:
        const form = new FormData();
        for (const f of files) form.append('files[]', f);
        const res = await fetch(`${API_URL}/admin/audio/uploads`, { method:'POST', headers: authHeaders(), body: form });
        return handle(res);
        */
        // по одному:
        // eslint-disable-next-line no-await-in-loop
        const r = await audioUpload(files[i], p => onEachProgress?.(i, p));
        results.push(r);
    }
    return results;
}

/* -------- Устройства --------
   GET /admin/audio/devices
-------------------------------- */
export async function devicesList() {
    const res = await fetch(`${API_URL}/admin/audio/devices`, { headers: authHeaders() });
    return handle(res);
}

/* -------- Плейлист АУДИО устройства (по Device)
   GET    /admin/audio/playlist/{deviceId}
   POST   /admin/audio/playlist/{deviceId}
   POST   /admin/audio/playlist/{deviceId}/attach
   DELETE /admin/audio/playlist/{deviceId}/detach/{audioId}
   POST   /admin/audio/playlist/{deviceId}/reorder
-------------------------------- */
export async function getDeviceAudioPlaylist(deviceId) {
    const res = await fetch(`${API_URL}/admin/audio/playlist/${deviceId}`, { headers: authHeaders() });
    return handle(res);
}
export async function setDeviceAudioPlaylist(deviceId, items /* [{hash,url,size?,position?,mime?,duration?}] */) {
    const res = await fetch(`${API_URL}/admin/audio/playlist/${deviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ items })
    });
    return handle(res);
}
export async function attachAudioToDevice(deviceId, audioId, position /* optional */) {
    const res = await fetch(`${API_URL}/admin/audio/playlist/${deviceId}/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(position == null ? { audioId } : { audioId, position })
    });
    return handle(res);
}
export async function detachAudioFromDevice(deviceId, audioId) {
    const res = await fetch(`${API_URL}/admin/audio/playlist/${deviceId}/detach/${audioId}`, {
        method: 'DELETE',
        headers: authHeaders()
    });
    return handle(res);
}
export async function reorderDeviceAudio(deviceId, orders /* [{audioId, position}] */) {
    const res = await fetch(`${API_URL}/admin/audio/playlist/${deviceId}/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ orders })
    });
    return handle(res);
}

/* -------- Устройства для конкретного аудио (аудио-центрично)
   GET  /admin/audio/audio/{audioId}/devices
   POST /admin/audio/audio/{audioId}/devices
   POST /admin/audio/audio/{audioId}/attach
   DELETE /admin/audio/audio/{audioId}/detach/{deviceId}
   POST /admin/audio/audio/{audioId}/reorder
-------------------------------- */
export async function getDevicesForAudio(audioId) {
    const res = await fetch(`${API_URL}/admin/audio/audio/${audioId}/devices`, { headers: authHeaders() });
    return handle(res);
}
export async function setDevicesForAudio(audioId, devices /* [{deviceId, position?}] */) {
    const res = await fetch(`${API_URL}/admin/audio/audio/${audioId}/devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ devices })
    });
    return handle(res);
}
export async function attachDeviceToAudio(audioId, deviceId, position /* optional */) {
    const res = await fetch(`${API_URL}/admin/audio/audio/${audioId}/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(position == null ? { deviceId } : { deviceId, position })
    });
    return handle(res);
}
export async function detachDeviceFromAudio(audioId, deviceId) {
    const res = await fetch(`${API_URL}/admin/audio/audio/${audioId}/detach/${deviceId}`, {
        method: 'DELETE',
        headers: authHeaders()
    });
    return handle(res);
}
export async function reorderDevicesForAudio(audioId, orders /* [{deviceId, position}] */) {
    const res = await fetch(`${API_URL}/admin/audio/audio/${audioId}/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ orders })
    });
    return handle(res);
}

/* -------- деприкейт-заглушки -------- */
export async function audioPlaylists() { throw new Error('Not implemented.'); }
export async function audioPlaylistCreate() { throw new Error('Not implemented.'); }
export async function audioPlaylistGet() { throw new Error('Not implemented.'); }
export async function audioPlaylistDelete() { throw new Error('Not implemented.'); }
export async function audioPlaylistAddItem() { throw new Error('Not implemented.'); }
export async function audioPlaylistRemoveItem() { throw new Error('Not implemented.'); }
export async function audioPlaylistReorder() { throw new Error('Not implemented.'); }
